/* eslint-disable no-magic-numbers */

import { useReactiveVar } from '@apollo/client/react';
import Drawer from 'components/Drawer/Drawer';
import { EditInstallerForm } from 'components/Drawer/types';
import Modal from 'components/Modal/Modal';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useInstallers from 'hooks/useInstallers/useInstallers';
import useSortingInstallersTable from 'hooks/useSorting/useSortingInstallersTable/useSortingInstallersTable';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { kebabCase } from 'lodash';
import React, { useState } from 'react';
import { authVar } from 'state/vars';
import styled from 'styled-components';
import Button from 'ui/atoms/Button/Button';
import TableCell from 'ui/atoms/TableCell/TableCell';
import TableCellStatus from 'ui/atoms/TableCellStatus/TableCellStatus';
import TableHeader from 'ui/atoms/TableHeader/TableHeader';
import NoInstallers from 'ui/molecules/NoInstallers/NoInstallers';
import Search from 'ui/molecules/Search/SearchMUI';
import TableHeaderRow from 'ui/molecules/TableHeaderRow/TableHeaderRow';
import TableRow from 'ui/molecules/TableRow/TableRow';
import Table from 'ui/organisms/Table/Table';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import PageContent from 'ui/templates/PageContent/PageContent';
import { StickyToTop } from 'ui/templates/StickyToTop/StickyToTop';
import ArrayUtil from 'utils/Array/Array.util';
import { filterBySearchTerm } from 'utils/FilterBySearchTerm/FilterBySearchTerm.util';
import { createUsersTabs } from '../usersTabsConst';

const TableWrapper = styled.div`
  height: calc(100vh - 24rem);
  width: calc(100vw - 25rem);
  margin-right: 1rem;
`;
export const createHeaders = (translation: Translation) => [
  translation.installer,
  translation.atlas_access_app,
  translation.atlas_config_app
];

const Installers: React.FC = () => {
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const isEntraLess = user?.isEntraLess!;
  const usersTabs = createUsersTabs(translation, isEntraLess);
  const headers = createHeaders(translation);
  const [filterStr, setFilterStr] = useState('');
  const { showDrawer } = useDrawer();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const skeletonArray = ArrayUtil.SkeletonArray();
  const { installersGroups, loading: installersLoading } = useInstallers({
    handleFetchError
  });
  const { handleSortingIcon, handleSortingOnClick } = useSortingInstallersTable(translation);

  const handleAddInstaller = () => {
    showDrawer({
      type: 'addInstaller',
      contentValue: {}
    });
  };

  const handleRowClick = (installer: EditInstallerForm) => {
    showDrawer({
      type: 'editInstaller',
      contentValue: {
        ...installer
      }
    });
  };

  const getFilterData = () => {
    if (installersGroups && installersGroups[0])
      return filterBySearchTerm(installersGroups[0].members, filterStr, (element) => {
        const hasRegisteredConfigDevice = element.mobileDevices.some(
          (device) => device.role === 'CONFIG' && device.isRegistered
        );
        const hasRegisteredAccessDevice = element.mobileDevices.some(
          (device) => device.role === 'ACCESS' && device.isRegistered
        );
        return [
          element.name,
          element.email,
          hasRegisteredConfigDevice ? translation.connected : translation.disconnected,
          hasRegisteredAccessDevice ? translation.connected : translation.disconnected
        ];
      });
    return [];
  };

  const isTableVisible = () => installersLoading || (!installersLoading && installersGroups.length > 0);
  const isTableLoadingVisible = () => installersLoading;
  const isTableRowsWithDataVisible = () => !installersLoading && installersGroups.length > 0;
  const isTableRowsWithNoDataVisible = () =>
    !installersLoading &&
    installersGroups.length > 0 &&
    installersGroups[0] &&
    installersGroups[0].members.length === 0;

  const handleClearButtonOnClick = () => {
    setFilterStr('');
  };

  return (
    <PageContent title="ATLAS" titleId="location" tabs={usersTabs} id="installers-page">
      <StickyToTop>
        <ComponentWrapper alignItems="center" justifyContent="space-between" gap="0.5rem" width="100%">
          <Search
            style={{ width: '15rem' }}
            onChange={(event) => setFilterStr(event.target.value)}
            value={filterStr}
            clearButtonOnClick={handleClearButtonOnClick}
          />
          <Button
            style={{ width: '12rem' }}
            onClick={handleAddInstaller}
            id="add-installer-button"
            textColor="lTextHigh"
            backgroundColor="primary"
          >
            {translation.add_user}
          </Button>
        </ComponentWrapper>
      </StickyToTop>
      {isTableVisible() && (
        <TableWrapper>
          <Table
            header={
              <TableHeaderRow placeForArrow>
                <TableHeader
                  headerText={headers[0]}
                  id={kebabCase(`header-${headers[0]}`)}
                  flex="0 0 33%"
                  iconSorting={handleSortingIcon(headers[0])}
                  onClick={() => handleSortingOnClick(headers[0])}
                />
                <TableHeader
                  headerText={headers[1]}
                  id={kebabCase(`header-${headers[1]}`)}
                  flex="0 0 33%"
                  iconSorting={handleSortingIcon(headers[1])}
                  onClick={() => handleSortingOnClick(headers[1])}
                />
                <TableHeader
                  headerText={headers[2]}
                  id={kebabCase(`header-${headers[2]}`)}
                  flex="0 0 33%"
                  iconSorting={handleSortingIcon(headers[2])}
                  onClick={() => handleSortingOnClick(headers[2])}
                />
              </TableHeaderRow>
            }
            headerId="lock-headers"
            rowsWrapperId="lock-list"
          >
            {isTableLoadingVisible() &&
              skeletonArray.map((_, index) => (
                <TableRow id={`row-skeleton-${index}`} marginAfterContent key={`skeletonTableRow-${_.id}`}>
                  <TableCell isLoading firstLineText="" flex="0 0 33%" />
                  <TableCell isLoading firstLineText="" flex="0 0 33%" />
                  <TableCell isLoading firstLineText="" flex="0 0 33%" />
                </TableRow>
              ))}
            {isTableRowsWithDataVisible() &&
              getFilterData().map((installer, id) => (
                <TableRow
                  onClick={() =>
                    handleRowClick({
                      email: installer.email || null,
                      id: installer.id,
                      mobileDevices: installer?.mobileDevices,
                      name: installer.name,
                      isEditable: installer.isEditable!
                    })
                  }
                  id={`row-${id}`}
                  hoverEffect
                  hoverArrow
                  key={installer.id}
                >
                  <TableCell
                    firstLineText={installer.name}
                    firstLineId={`row-${id}-name`}
                    secondLineText={installer.email || undefined}
                    secondLineId={`row-${id}-email`}
                    flex="0 0 33%"
                  />
                  <TableCellStatus
                    status={
                      installer?.mobileDevices?.find((device) => device.role === 'ACCESS' && device.isRegistered)
                        ?.isRegistered
                        ? 'connected'
                        : 'disconnected'
                    }
                    id={`row-${id}-status-access`}
                    flex="0 0 33%"
                  />
                  <TableCellStatus
                    status={
                      installer?.mobileDevices?.find((device) => device.role === 'CONFIG' && device.isRegistered)
                        ?.isRegistered
                        ? 'connected'
                        : 'disconnected'
                    }
                    id={`row-${id}-status-config`}
                    flex="0 0 33%"
                  />
                </TableRow>
              ))}
            {isTableRowsWithNoDataVisible() && (
              <ComponentWrapper
                height="calc(100vh - 24rem)"
                width="calc(100vw - 25rem)"
                margin="1rem"
                justifyContent="center"
                alignItems="center"
              >
                <NoInstallers handleOnButtonClick={handleAddInstaller} />
              </ComponentWrapper>
            )}
          </Table>
        </TableWrapper>
      )}

      <Drawer />
      <Modal />
    </PageContent>
  );
};

export default Installers;
